/**
 * The shop's FAQ — ONE module read by the browser bundle (the /faq page) and the Worker (the
 * server-rendered /faq page and its FAQPage JSON-LD).
 *
 * ⚠️ No DOM, no React, no Worker types: imported by the Vite bundle as well.
 *
 * Every answer is built from the same resolved facts the contact page prints (`ResolvedContact`:
 * SHOP_COLLECT_* + SHOP_HOURS + BRAND_*), so changing a setting changes the FAQ and nothing here
 * is a second copy of an address or an opening time. 🔴 The collection point is the SHOP, never
 * the warehouse; this file never names either street.
 *
 * Wording that is a promise (collect rule, delivery times, returns) is the owner's, taken from the
 * brief of 2026-10-10. The returns answers are marked "owner to confirm wording" below.
 */
import { hoursRows, telHref, waHref, type ResolvedContact } from "./shop-contact";

/** A bit of an answer: plain text, an external link (new tab), or a link to a page of the shop. */
export type FaqSeg = string | { href: string; text: string } | { to: "signup" | "contact" | "clearance"; text: string };
export type FaqParagraph = FaqSeg[];

export interface FaqItem {
  id: string;
  q: string;
  /** Paragraphs; each a run of segments. */
  a: FaqParagraph[];
}
export interface FaqGroup {
  id: string;
  title: string;
  items: FaqItem[];
}

/** An answer as plain text (JSON-LD `acceptedAnswer.text`, meta descriptions). */
export function faqPlain(a: FaqParagraph[]): string {
  return a.map((p) => p.map((s) => (typeof s === "string" ? s : s.text)).join("")).join("\n");
}

/** "Monday – Thursday 09:30–16:30; Friday 09:30–12:00 & 14:00–16:30; …" */
function hoursSentence(hours: ResolvedContact["hours"]): string {
  return hoursRows(hours).map((r) => `${r.days}: ${r.times}`).join("; ");
}

export function buildFaq(r: ResolvedContact): FaqGroup[] {
  const wa = waHref(r.contact.whatsapp);
  const waSeg: FaqSeg = wa ? { href: wa, text: `WhatsApp us on ${r.contact.whatsapp}` } : `WhatsApp us on ${r.contact.whatsapp}`;
  return [
    {
      id: "ordering",
      title: "Ordering & payment",
      items: [
        {
          id: "how-to-order",
          q: "How do I order?",
          a: [["Browse or search the shop, add what you want to your cart, choose delivery or collection, pay, and we email you a confirmation."]],
        },
        {
          id: "payment",
          q: "How can I pay?",
          a: [
            ["By card or instant EFT, through PayFast. Checkout is secure and we never see your card number."],
            ["We do not offer cash on delivery."],
          ],
        },
      ],
    },
    {
      id: "delivery",
      title: "Delivery & collection",
      items: [
        {
          id: "where-collect",
          q: "Where and when can I collect?",
          a: [
            [`Collect from ${r.collect.name}, ${r.collect.address}. `, { href: r.collect.mapUrl, text: "Open the map" }, "."],
            [`Trading hours: ${hoursSentence(r.hours)}.`],
          ],
        },
        {
          id: "collect-when",
          q: "How soon can I collect my order?",
          a: [
            ["If the shop has it, you can collect the same day (orders up to an hour before closing). If it comes from our warehouse, it is ready the next day the shop is open. Collecting is free."],
          ],
        },
        {
          id: "delivery-time",
          q: "How long does delivery take?",
          a: [["2–3 working days to main centres, 3–7 working days to outlying & regional areas."]],
        },
        {
          id: "delivery-fee",
          q: "How much does delivery cost?",
          a: [["Free on retail orders of R2,000 or more (after any discount), wherever our courier delivers. Below that, the fee is calculated at checkout from your parcel size and address by our courier partners, so you see it before you pay."]],
        },
        {
          id: "oversized",
          q: "What about big or heavy orders?",
          a: [[ "For oversized or heavy orders, collect from the shop or ", waSeg, " for a quote." ]],
        },
        {
          id: "tracking",
          q: "How do I track my parcel?",
          a: [["Once the courier collects your parcel we email you the tracking number."]],
        },
      ],
    },
    {
      id: "returns",
      title: "Returns",
      items: [
        {
          id: "returns-policy",
          q: "What is your returns policy?",
          // Owner decision 2026-10-30: 14 days; in-store returns free; courier return of a non-faulty item paid by the customer.
          // The shop's name comes from the SHOP_COLLECT_* settings, like every other collect fact here.
          a: [[`14-day returns on unused goods in original packaging. Returning in person at ${r.collect.name} is free; for a courier return of an item that is not faulty, you pay the return courier. Faulty, damaged or wrong items are always free to return — we arrange the collection.`]],
        },
        {
          id: "faulty",
          q: "What if something is faulty?",
          // Source: South Africa's Consumer Protection Act implied 6-month warranty on defective goods.
          // Owner to confirm wording.
          a: [
            ["Defective goods are covered by the Consumer Protection Act's implied 6-month warranty: we repair, replace or refund."],
            ["To start a return, ", waSeg, " with your order number."],
          ],
        },
      ],
    },
    {
      id: "wholesale",
      title: "Wholesale",
      items: [
        {
          id: "wholesale-account",
          q: "How do I get a wholesale account?",
          a: [
            ["Apply through ", { to: "signup", text: "sign-up" }, ". We approve your account, then wholesale prices show whenever you are signed in."],
          ],
        },
      ],
    },
    {
      id: "deals",
      title: "Deals",
      items: [
        {
          id: "deal-of-the-day",
          q: "How does Deal of the Day work?",
          a: [["The deal changes at midnight (South African time). Each order has a limit while stock lasts."]],
        },
        {
          id: "clearance",
          q: "What is Clearance?",
          a: [["Items we have reduced to clear. See them on the ", { to: "clearance", text: "Clearance page" }, "."]],
        },
      ],
    },
  ];
}

/** The "still need help" block: WhatsApp / phone / email from BRAND_*. */
export function faqContactLines(r: ResolvedContact): Array<{ label: string; href: string }> {
  const out: Array<{ label: string; href: string }> = [];
  const wa = waHref(r.contact.whatsapp);
  const tel = telHref(r.contact.phone);
  if (wa) out.push({ label: `WhatsApp ${r.contact.whatsapp}`, href: wa });
  if (tel) out.push({ label: `Call ${r.contact.phone}`, href: tel });
  out.push({ label: r.contact.email, href: `mailto:${r.contact.email}` });
  return out;
}

/** schema.org FAQPage. Every question and answer appears on the page as visible text. */
export function faqPageLd(groups: FaqGroup[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: groups.flatMap((g) =>
      g.items.map((i) => ({ "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: faqPlain(i.a) } })),
    ),
  };
}
